import type {
  MotionAlignmentConfig,
  MotionColorInput,
  MotionDocumentColor,
  MotionConnectionRef,
  MotionControlledField,
  MotionEditableField,
  MotionEditableLayerKind,
  MotionFontReplacement,
  MotionFontPreview,
  MotionReplaceMediaConfig,
  MotionTargetQuery,
} from "@blings/blings-player/editor-tools";

export const ANCHOR_POSITIONS = [
  { value: "topLeft", label: "Top left" },
  { value: "topCenter", label: "Top center" },
  { value: "topRight", label: "Top right" },
  { value: "middleLeft", label: "Middle left" },
  { value: "middleCenter", label: "Center" },
  { value: "middleRight", label: "Middle right" },
  { value: "bottomLeft", label: "Bottom left" },
  { value: "bottomCenter", label: "Bottom center" },
  { value: "bottomRight", label: "Bottom right" },
] as const;

export type AnchorPosition = typeof ANCHOR_POSITIONS[number]["value"];
export type EditableProperty = string;
export type VisualEditableField = MotionEditableField &
  Partial<
    Pick<
      MotionControlledField,
      | "jsonValue"
      | "effectiveValue"
      | "controlled"
      | "controls"
      | "controlState"
      | "writePolicy"
    >
  > & {
    isMixedValue?: boolean;
    targetFields?: VisualEditableField[];
  };
export type FieldLookup = Map<EditableProperty, VisualEditableField>;

export type ApplyFieldValue = (
  field: VisualEditableField,
  value: unknown
) => void;

export type ApplyLayerFont = (replacement: MotionFontReplacement) => void;

export type RemoveFieldValue = (field: VisualEditableField) => void;

export type ApplyAnchorPosition = (
  field: VisualEditableField,
  position: AnchorPosition
) => void;

export type FreezeAnimatedFields = (fields: VisualEditableField[]) => void;

export type FieldConnectionActions = {
  getModIdByIndex: (modIndex: number) => string | undefined;
  onOpenMod: (modId: string) => void;
  onHoverMod: (modId?: string) => void;
};

export type OpenVisualPropertyConnectorOptions = {
  componentIndex?: number;
  preferredType?: "documentColor" | "media";
  selectedColor?: string;
};

export type OpenVisualPropertyConnector = (
  fields: Array<VisualEditableField | undefined>,
  options?: OpenVisualPropertyConnectorOptions
) => void;

export type FieldConnectionRef = MotionConnectionRef;

export type VisualPropertyTarget = MotionTargetQuery;

export type AlignLayer = (config: MotionAlignmentConfig) => void;

export type ReplaceMedia = (config: MotionReplaceMediaConfig) => void;

export type LayerKind = MotionEditableLayerKind;

export type DocumentDimension = "width" | "height";

export type ApplyDocumentSize = (
  dimension: DocumentDimension,
  value: number
) => void;

export type ApplyDocumentDuration = (durationFrames: number) => void;

export type DocumentMediaKind = "image" | "video" | "audio";

export type DocumentColor = MotionDocumentColor;

export type ReplaceDocumentColor = (
  color: DocumentColor,
  to: MotionColorInput
) => void;

export type DocumentMediaAsset = {
  id: string;
  kind: DocumentMediaKind;
  layerName: string;
  layerUid?: number;
  layerPath: string;
  assetId: string;
  assetName: string;
  currentUrl: string;
  occurrenceCount: number;
};

export type ReplaceDocumentMedia = (
  asset: DocumentMediaAsset,
  config: MotionReplaceMediaConfig
) => void;

export type FontSelectOption = {
  value: string;
  label: string;
  replacement: MotionFontReplacement;
  source: "animation" | "account" | "google";
  fontName?: string;
  family?: string;
  style?: "normal" | "italic";
  styleName?: string;
  weight?: string;
  weightName?: string;
  stylesheetUrl?: string;
  familyPreview?: MotionFontPreview;
  preview?: MotionFontPreview;
};
